/**
 * Schema for the data-driven, MODDABLE game content.
 *
 * Layout (each category is an index-driven folder under /public/data):
 *   units/        shared base unit defs (concrete defaults) + index.json
 *   buildings/    shared base building defs + index.json
 *   resources/    resource defs + index.json
 *   resourceNodes/ resource-node defs + index.json
 *   civs/         one self-contained file per civ + index.json
 *   maps/         map defs + index.json
 *
 * Civs are flat & explicit: a civ unit/building either references a shared `base`
 * and overrides specific attributes, OR defines a brand-new (civ-unique) entity.
 * There is exactly ONE shallow merge layer (base -> civ override). No prototype
 * `extends` chains, no separately-applied bonus multipliers — what you see in a
 * civ file is what you get. A mod is a file dropped in + an index entry.
 */

export type ResourceId = "food" | "wood" | "gold" | "stone";
export type ResourceCost = Partial<Record<ResourceId, number>>;

export type EntityClass =
  | "worker"
  | "infantry"
  | "ranged"
  | "cavalry"
  | "priest"
  | "siege"
  | "naval";

/** A single attack profile. */
export interface AttackDef {
  damage: number;
  /** Tiles. 1 = melee. */
  range: number;
  /** Seconds between hits. */
  cooldown: number;
  /** Optional damage bonus multipliers vs a class. */
  bonus?: Partial<Record<EntityClass, number>>;
  /** Flat damage multiplier vs any BUILDING (buildings have no EntityClass, so
   *  `bonus[cls]` can't target them). Cannon ships use this for siege. */
  bonusBuilding?: number;
}

/** A healer profile (priest). Restores hp to nearby friendly units. */
export interface HealDef {
  /** Tiles. Friendly units within this range are healed. */
  range: number;
  /** Hit points restored per second. */
  rate: number;
}

/** A declarative area effect emitted by a unit/building. Consumed by a generic
 *  aura system (a later phase); allowed & validated here so content can declare it. */
export interface AuraDef {
  /** Radius in tiles. */
  radius: number;
  /** Who it affects. */
  affects: "ally" | "enemy" | "all";
  /** Optional class filter. */
  targetClass?: EntityClass[];
  /** Which stat it modifies (e.g. "gatherRate", "damage", "armor", "speed"). */
  stat: string;
  /** Multiplicative or additive. */
  mode: "mul" | "add";
  amount: number;
}

/**
 * A fully-resolved unit definition. Shared base files (units/*.json) provide
 * concrete defaults; a civ entry merges over a base (or stands alone).
 */
export interface UnitDef {
  id: string;
  name: string;
  class: EntityClass;
  hp: number;
  /** Tiles per second. */
  speed: number;
  /** Line of sight in tiles. */
  los: number;
  cost: ResourceCost;
  /** Seconds to train. */
  buildTime: number;
  /** Population slots consumed. */
  population: number;
  attack?: AttackDef;
  /** Healer profile (priests). */
  heal?: HealDef;
  armor: number;
  radius?: number;
  /** Civ unique unit (limited, highlighted in UI). */
  unique?: boolean;
  /** Resolved civ id (set by the registry for civ units). */
  civ?: string;
  // Worker abilities
  canBuild?: boolean;
  canGather?: boolean;
  carryingCapacity?: number;
  /** Garrison/transport capacity in POPULATION points (naval transports + future
   *  garrisonable units). 0 / undefined = carries nothing. A 4-pop siege fills 4. */
  capacity?: number;
  /** Which building trains this unit. */
  trainingBuilding: string;
  /** Capability tags read by generic systems / the bot (e.g. "crossWater"). */
  capabilities?: string[];
  /** Classes this unit is strong against (bot army-composition hints). */
  counters?: EntityClass[];
  auras?: AuraDef[];
}

/** A civ's unit entry: reference a shared `base` and override attributes, or omit
 *  `base` to define a fully civ-unique unit. */
export type CivUnitEntry = Partial<UnitDef> & {
  base?: string;
  name: string;
};

export interface BuildingDef {
  id: string;
  name: string;
  hp: number;
  los: number;
  cost: ResourceCost;
  /** Seconds to construct. */
  buildTime: number;
  /** Footprint in tiles [w, h]. */
  size: [number, number];
  /** Population capacity provided. */
  population?: number;
  /** If true, resource carriers drop off here. */
  isDropSite?: boolean;
  /** If true, losing all of these (and all units) defeats the player. */
  isCore?: boolean;
  /** Where this building may be placed. */
  placement?: "land" | "waterEdge";
  /** Worked-resource (field) config. */
  gatherResource?: ResourceId;
  gatherCap?: number;
  gatherAmount?: number;
  gatherRegen?: number;
  gatherTime?: number;
  gatherPerGather?: number;
  /** Capability tags of what this building trains (e.g. "military", "navalTransport"). */
  trains?: string[];
  /** Capability tags this building provides (e.g. "population", "dropSite"). */
  provides?: string[];
  capabilities?: string[];
  auras?: AuraDef[];
  /** A rampart/pier structure. On land its rendered sub-tile geometry blocks
   *  movement; on water its occupied tile is a solid pier for ships. */
  solid?: boolean;
  /** A controllable opening in a wall: closed to enemies, but the owner's (and
   *  allies') land units use its physical portal. Takes precedence over `solid`
   *  when a mod explicitly supplies both flags. */
  gate?: boolean;
  /** Garrison capacity in POPULATION points (a tower holds soldiers/workers who
   *  each add one arrow to its volley). */
  garrison?: number;
  /** Defensive attack profile (a tower fires this once PER garrisoned unit). */
  attack?: AttackDef;
}

/** A civ's building entry: override a shared base's attributes, or omit `base` for
 *  a civ-unique building (e.g. an Aliens "spaceship_factory" — a full def). */
export type CivBuildingEntry = Partial<BuildingDef> & {
  base?: string;
};

export interface ResourceDef {
  id: ResourceId;
  name: string;
  color: string;
}

export interface ResourceNodeDef {
  id: string;
  name: string;
  resource: ResourceId;
  amount: number;
  gatherTime: number;
  perGather: number;
  color: string;
}

export interface CivDef {
  id: string;
  name: string;
  color: string;
  description: string;
  /** unitId -> entry (base override or civ-unique). */
  units: Record<string, CivUnitEntry>;
  /** buildingId -> entry (base override or civ-unique). Optional. */
  buildings?: Record<string, CivBuildingEntry>;
}

export interface PlayerStart {
  id: number;
  civ: string;
  color: string;
  kind: "human" | "bot";
  /** Alliance team (players sharing a team are allies). Default = player id. */
  team?: number;
  /** Optional authoring hint; actual start hex is derived from the hex grid. */
  position?: [number, number];
}

export interface StartingEntity {
  player: number;
  /** For units: a base/role (e.g. "worker") resolved per civ. For buildings: an id. */
  type: string;
  kind: "unit" | "building";
  position?: [number, number];
}

/** Terrain recipe names accepted by map data. `forest_floor` paints forest. */
export const TERRAIN_TYPES = ["grass", "forest_floor", "water", "rock", "sand"] as const;
export type TerrainType = typeof TERRAIN_TYPES[number];

export interface TerrainFeature {
  type: TerrainType;
  count: number;
  minSize: number;
  maxSize: number;
}

/** The deterministic biome overlays needed to recreate map terrain. */
export interface TerrainRecipe {
  base?: TerrainType;
  features?: TerrainFeature[];
}

export interface TerrainGen extends TerrainRecipe {
  seed: number;
  startingResources?: { type: string; distance: number; count: number }[];
  scatteredResources?: { type: string; count: number; perCluster: number }[];
}

export interface MapDef {
  id: string;
  name: string;
  width: number;
  height: number;
  players: PlayerStart[];
  startingResources: ResourceCost;
  startingEntities: StartingEntity[];
  terrain: TerrainGen;
}

export interface GameData {
  resources: Record<string, ResourceDef>;
  resourceNodes: Record<string, ResourceNodeDef>;
  /** Shared base unit defs (concrete defaults). */
  units: Record<string, UnitDef>;
  /** Shared base building defs. */
  buildings: Record<string, BuildingDef>;
  civs: Record<string, CivDef>;
  maps: Record<string, MapDef>;
}
